/* atd-all-blogs.js (data/search/filter/pagination/highlight/URL-sync)
   - Filters by metafield VALUES (checkbox value + <li data-*-list="...">), not handles.
   - "Other" behavior: if selected in a group, items with EMPTY list for that group match.
   - AND across groups, OR within a group; text search; pagination; highlight; URL sync.
   - Hides pagination when filtered results <= page size, and shows a friendly empty-state message when 0 results.
   - Hides the <ul.atd-blog__articles> when there are 0 results (same idea as pagination).
*/
(() => {
  const ROOT = '.atd-all-blogs';
  const PAGE_SIZE_FALLBACK = 6;

  const qsa = (r, s) => Array.from(r.querySelectorAll(s));
  const getURL = () => new URL(window.location.href);
  const splitList = (s, sep = '|') =>
    (s || '')
      .split(sep)
      .map(v => v.trim())
      .filter(Boolean);

  // --- highlight helpers ---
  // CHANGED: use actual classes from atd-article-card snippet
  const HIGHLIGHT_TARGETS = ['.atd-article__title', '.atd-article__excerpt'];

  const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const clearHighlightsInCard = (li) => {
    HIGHLIGHT_TARGETS.forEach(sel => {
      qsa(li, sel).forEach(node => {
        if (node.dataset.origHtml != null) node.innerHTML = node.dataset.origHtml;
        else node.dataset.origHtml = node.innerHTML;
      });
    });
  };
  const applyHighlightsInCard = (li, query) => {
    if (!query) { clearHighlightsInCard(li); return; }
    const re = new RegExp(`(${escapeRegExp(query)})`, 'gi');
    HIGHLIGHT_TARGETS.forEach(sel => {
      qsa(li, sel).forEach(node => {
        const orig = (node.dataset.origHtml != null)
          ? node.dataset.origHtml
          : (node.dataset.origHtml = node.innerHTML, node.innerHTML);
        node.innerHTML = orig.replace(re, '<mark class="atd-search__hl">$1</mark>');
      });
    });
  };
  // --- end highlight helpers ---

  const parseCsv = (s) =>
    (s || '')
      .split(',')
      .map(v => v.trim())
      .filter(Boolean);

  const getParams = () => {
    const u = getURL();
    const p = parseInt(u.searchParams.get('page') || '1', 10);
    return {
      q: (u.searchParams.get('q') || '').trim(),
      page: Number.isFinite(p) && p > 0 ? p : 1,
      type: parseCsv(u.searchParams.get('type')),
      product: parseCsv(u.searchParams.get('product')),
      application: parseCsv(u.searchParams.get('application')),
    };
  };

  const setParams = (params) => {
    const u = getURL();
    Object.entries(params).forEach(([k, v]) => {
      if (Array.isArray(v)) {
        if (v.length) u.searchParams.set(k, v.join(','));
        else u.searchParams.delete(k);
      } else if (!v || (k === 'page' && v === 1)) {
        u.searchParams.delete(k);
      } else {
        u.searchParams.set(k, String(v));
      }
    });
    window.history.replaceState({}, '', u.toString());
  };

  const debounce = (fn, ms = 250) => { let t; return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); }; };

  // Read currently checked VALUES (not handles)
  const readSelectedValues = (root) => {
    const out = { type: [], product: [], application: [] };
    qsa(root, '.atd-filter__group').forEach(group => {
      const key = group.dataset.filterKey;
      if (!key) return;
      const vals = qsa(group, '.atd-filter__cb:checked')
        .map(cb => (cb.value || '').trim())
        .filter(Boolean);
      out[key] = Array.from(new Set(vals));
    });
    return out;
  };

  // Apply URL params to checkbox UI (match by VALUE)
  const applyParamsToUI = (root, params) => {
    const want = {
      type: new Set((params.type || []).map(x => x.toLowerCase())),
      product: new Set((params.product || []).map(x => x.toLowerCase())),
      application: new Set((params.application || []).map(x => x.toLowerCase())),
    };
    qsa(root, '.atd-filter__group').forEach(group => {
      const key = group.dataset.filterKey;
      if (!key) return;
      qsa(group, '.atd-filter__cb').forEach(cb => {
        const val = (cb.value || '').trim().toLowerCase();
        cb.checked = want[key]?.has(val) || false;
      });
    });
  };

  // Group matcher
  const matchGroup = (li, key, selected) => {
    if (!selected || !selected.length) return true;
    const selectedLC = selected.map(s => s.toLowerCase());
    const values = splitList(li.getAttribute(`data-${key}-list`)).map(s => s.toLowerCase());
    const hasValues = values.length > 0;

    const otherPicked = selectedLC.includes('other');
    if (otherPicked && !hasValues) return true;

    const picksNoOther = selectedLC.filter(v => v !== 'other');
    if (!picksNoOther.length) return false;
    return values.some(v => picksNoOther.includes(v));
  };

  // CHANGED: make sure we actually read the excerpt node and search it.
  // This now searches:
  // - title text
  // - excerpt_or_content text (in .atd-article__excerpt)
  // - everything else in the <li> (author, date, etc.)
  const textMatches = (li, q) => {
    if (!q) return true;
    const title = li.querySelector('.atd-article__title');
    const excerpt = li.querySelector('.atd-article__excerpt');
    const txt = `${title ? title.textContent : ''} ${excerpt ? excerpt.textContent : ''} ${li.textContent}`.toLowerCase();
    return txt.includes(q.toLowerCase());
  };
  // END CHANGED

  const renderPage = (items, page, perPage) => {
    const totalPages = Math.max(1, Math.ceil(items.length / perPage));
    const current = Math.min(Math.max(1, page), totalPages);
    const start = (current - 1) * perPage;
    const end = start + perPage;
    items.forEach((li, i) => { li.hidden = !(i >= start && i < end); });
    return { current, totalPages, visible: items.slice(start, end), totalItems: items.length };
  };

  // CHANGED: pagination window logic (show 4 pages max; show all if <=4; manage ellipses)
  const updatePaginationUI = (root, current, totalPages) => {
    const pager = root.querySelector('.atd-all-blogs__pagination');
    if (!pager) return;

    const buttons = Array.from(pager.querySelectorAll('.atd-pagination__btn--page'));
    const prev = pager.querySelector('.atd-pagination__btn--prev');
    const next = pager.querySelector('.atd-pagination__btn--next');
    const leftEll = pager.querySelector('.atd-pagination__ellipses[data-side="left"]');
    const rightEll = pager.querySelector('.atd-pagination__ellipses[data-side="right"]');

    // Determine the 4-slot window
    let start = 1;
    let end = totalPages;

    if (totalPages <= 4) {
      start = 1;
      end = totalPages;
      if (leftEll) { leftEll.hidden = true; leftEll.style.display = 'none'; }
      if (rightEll) { rightEll.hidden = true; rightEll.style.display = 'none'; }
    } else {
      // 4-page window
      if (current <= 2) {
        start = 1;
        end = 4;
      } else if (current >= totalPages - 1) {
        start = totalPages - 3;
        end = totalPages;
      } else {
        // Center window: one left, current, and two to the right
        start = current - 1;
        end = current + 2;
      }
      if (leftEll) { const show = start > 1; leftEll.hidden = !show; leftEll.style.display = show ? '' : 'none'; }
      if (rightEll) { const show = end < totalPages; rightEll.hidden = !show; rightEll.style.display = show ? '' : 'none'; }
    }

    // Show only start..end page buttons
    buttons.forEach((b) => {
      const num = parseInt(b.dataset.page, 10);
      const show = Number.isFinite(num) && num >= start && num <= end;
      b.hidden = !show;
      b.style.display = show ? '' : 'none';
      b.classList.toggle('is-active', num === current);
      if (num === current && show) b.setAttribute('aria-current', 'page'); else b.removeAttribute('aria-current');
    });

    if (prev) prev.disabled = current <= 1;
    if (next) next.disabled = current >= totalPages;
  };
  // END CHANGED

  const togglePaginationVisibility = (root, show) => {
    const pager = root.querySelector('.atd-all-blogs__pagination');
    if (!pager) return;
    pager.style.display = show ? '' : 'none';
    pager.hidden = !show;
  };

  // show/hide the <ul.atd-blog__articles>
  const toggleArticlesVisibility = (root, show) => {
    const list = root.querySelector('.atd-blog__articles');
    if (!list) return;
    list.style.display = show ? '' : 'none';
    list.hidden = !show;
  };

  const getOrCreateEmptyState = (root) => {
    let msg = root.querySelector('.atd-empty--filters');
    if (!msg) {
      msg = document.createElement('p');
      msg.className = 'atd-empty atd-empty--filters';
      msg.setAttribute('role', 'status');
      msg.setAttribute('aria-live', 'polite');
      msg.style.display = 'none';
      msg.textContent = 'No articles found.';
      const content = root.querySelector('.atd-all-blogs__content') || root;
      content.appendChild(msg);
    }
    return msg;
  };

  const showEmptyState = (root, show) => {
    const msg = getOrCreateEmptyState(root);
    msg.style.display = show ? '' : 'none';
    msg.hidden = !show;
  };

  const mount = (root) => {
    const list = root.querySelector('.atd-blog__articles');
    if (!list) return;

    const perPage = parseInt(root.querySelector('.atd-all-blogs__pagination')?.dataset.pageSize || '', 10) || PAGE_SIZE_FALLBACK;

    const allItems = qsa(list, ':scope > .atd-article__container');
    const searchInput = root.querySelector('.atd-search__input');
    const pager = root.querySelector('.atd-all-blogs__pagination');

    // Initial state from URL
    const state = getParams();
    if (searchInput) searchInput.value = state.q || '';
    applyParamsToUI(root, state);

    const recompute = (desiredPage = 1, syncFromUI = true) => {
      const urlParams = getParams();
      const selected = syncFromUI ? readSelectedValues(root) : {
        type: urlParams.type, product: urlParams.product, application: urlParams.application
      };
      const q = (searchInput ? searchInput.value.trim() : urlParams.q || '').trim();

      // Filter
      const filtered = allItems.filter(li =>
        textMatches(li, q) &&
        matchGroup(li, 'type', selected.type) &&
        matchGroup(li, 'product', selected.product) &&
        matchGroup(li, 'application', selected.application)
      );

      // Clear & render
      allItems.forEach(li => { clearHighlightsInCard(li); li.hidden = true; });
      const { current, totalPages, visible, totalItems } = renderPage(filtered, desiredPage, perPage);
      if (q) visible.forEach(li => applyHighlightsInCard(li, q));

      // Empty state + visibility controls
      if (totalItems === 0) {
        showEmptyState(root, true);
        toggleArticlesVisibility(root, false);
        togglePaginationVisibility(root, false);
      } else {
        showEmptyState(root, false);
        toggleArticlesVisibility(root, true);
        const showPager = totalItems > perPage;
        togglePaginationVisibility(root, showPager);
        if (showPager) updatePaginationUI(root, current, totalPages);
      }

      // URL sync with VALUES (not handles)
      setParams({
        q,
        page: totalItems > perPage ? current : 1,
        type: selected.type,
        product: selected.product,
        application: selected.application,
      });
    };

    // Listen to UI filter changes from atd-blogs-filter.js
    root.addEventListener('atd:filters:changed', () => recompute(1, true));

    // Search input debounce
    if (searchInput) searchInput.addEventListener('input', debounce(() => recompute(1, true), 250));

    // Pagination click handling
    if (pager) {
      pager.addEventListener('click', (e) => {
        const btn = e.target.closest('.atd-pagination__btn');
        if (!btn) return;
        const currentActive = pager.querySelector('.atd-pagination__btn.is-active');
        const current = currentActive ? parseInt(currentActive.dataset.page, 10) : 1;
        if (btn.classList.contains('atd-pagination__btn--prev')) return void recompute(Math.max(1, current - 1), true);
        if (btn.classList.contains('atd-pagination__btn--next')) return void recompute(current + 1, true);
        if (btn.classList.contains('atd-pagination__btn--page')) {
          const target = parseInt(btn.dataset.page, 10);
          if (Number.isFinite(target)) recompute(target, true);
        }
      });
    }

    // First paint (respect URL)
    recompute(state.page || 1, false);
  };

  const init = () => qsa(document, ROOT).forEach(mount);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
